// Challenge → toolset matcher. Given a user's free-text leadership challenge
// (their ILG), surface the most relevant frameworks plus a matching Lab and
// Diagnostic. Deterministic and dependency-light (keyword topics), so it can be
// used by the Home card, the Companion grounding, and the smoke suite alike.

import { FRAMEWORKS } from "./content";
import { LABS } from "./labs";
import { DIAGNOSTICS } from "./diagnostics";

export interface Recommendation {
  frameworks: { id: string; name: string }[];
  lab?: { id: string; title: string };
  diagnostic?: { id: string; title: string };
}

interface Topic {
  key: string;
  // word-boundary keywords (lowercase); matched against the challenge text
  keywords: string[];
  frameworks: string[]; // framework ids, most relevant first
  lab?: string; // lab id
  diagnostic?: string; // diagnostic id
}

// Ordered by specificity; every id below is validated by the smoke suite.
const TOPICS: Topic[] = [
  {
    key: "change",
    keywords: ["change", "transform", "transition", "reorg", "restructure", "rollout", "roll out", "adopt", "migrat", "resist", "buy-in", "buy in", "stalled", "stuck"],
    frameworks: ["change-equation", "change-curve"],
    lab: "stalled-change",
    diagnostic: "change-readiness",
  },
  {
    key: "speaking-up",
    keywords: ["speak", "voice", "say", "said", "silence", "silent", "raise", "candor", "candour", "feedback", "unspoken", "push back", "pushback", "tell", "honest", "difficult conversation", "tough conversation"],
    frameworks: ["truth", "advocacy-enquiry", "space", "three-traps"],
    lab: "unspoken-fix",
    diagnostic: "speaking-up",
  },
  {
    key: "safety",
    keywords: ["psychological safety", "safe", "safety", "mistake", "blame", "junior", "inclusion", "belong", "fear of", "afraid", "speak up", "status quo"],
    frameworks: ["clark", "stretch-safety"],
    lab: "challenger-safety",
    diagnostic: "psych-safety",
  },
  {
    key: "politics",
    keywords: ["politic", "stakeholder", "influence", "align", "senior", "vp", "exec", "board", "sponsor", "navigate", "leadership team", "manager", "boss", "upward"],
    frameworks: ["stakeholder-map", "baddeley", "sources-of-power"],
    lab: "map-the-room",
    diagnostic: "positioning",
  },
  {
    key: "team",
    keywords: ["team", "role", "goal", "accountab", "underperform", "dysfunction", "clarity", "ownership", "collaborat", "cross-functional", "cross functional"],
    frameworks: ["grip", "trust-equation"],
    lab: "grip-check",
    diagnostic: "grip-check",
  },
  {
    key: "tension",
    keywords: ["tension", "paradox", "balance", "trade-off", "tradeoff", "both", "dilemma", "competing", "conflicting", "either"],
    frameworks: ["paradox-map", "tension-grid", "paradox"],
    lab: "paradox-map",
  },
  {
    key: "trust",
    keywords: ["trust", "credibility", "credible", "reliab", "rapport", "relationship"],
    frameworks: ["trust-equation", "grip"],
    lab: "trust-audit",
  },
  {
    key: "culture",
    keywords: ["culture", "values", "norms", "behaviour", "behavior", "environment", "ways of working", "subculture"],
    frameworks: ["double-s", "cultural-web", "schein-mechanisms", "ego-eco"],
    lab: "read-your-culture",
  },
  {
    key: "presence",
    keywords: ["present", "presence", "on the spot", "nervous", "confidence", "confident", "pitch", "audience", "charisma", "gravitas", "public speaking", "stage"],
    frameworks: ["charisma", "speech-structures", "fear"],
    lab: "land-it",
  },
  {
    key: "curiosity",
    keywords: ["curiosity", "curious", "judge", "judgement", "judgment", "assumption", "label", "observe", "perspective", "bias", "jump to"],
    frameworks: ["close-looking", "picl"],
    lab: "look-before-label",
  },
  {
    key: "meetings",
    keywords: ["meeting", "agenda", "1:1", "one-to-one", "one to one", "standup", "stand-up", "discussion"],
    frameworks: ["conversation-types", "doing-spacious"],
    lab: "audit-a-meeting",
  },
];

function scoreTopic(t: Topic, text: string): number {
  let score = 0;
  for (const kw of t.keywords) if (text.includes(kw)) score += kw.includes(" ") ? 2 : 1;
  return score;
}

/**
 * Recommend frameworks + a Lab + a Diagnostic for a free-text challenge.
 * Returns null when the text is empty or matches nothing recognisable.
 */
export function recommendForChallenge(challenge: string): Recommendation | null {
  const text = (challenge || "").toLowerCase();
  if (!text.trim()) return null;

  const ranked = TOPICS.map((t) => ({ t, s: scoreTopic(t, text) }))
    .filter((r) => r.s > 0)
    .sort((a, b) => b.s - a.s);
  if (!ranked.length) return null;

  // Frameworks: walk topics best-first, take up to 3 distinct, valid ids.
  const fwIds: string[] = [];
  for (const { t } of ranked) {
    for (const id of t.frameworks) {
      if (fwIds.length >= 3) break;
      if (!fwIds.includes(id) && FRAMEWORKS[id]) fwIds.push(id);
    }
  }
  const frameworks = fwIds.map((id) => ({ id, name: FRAMEWORKS[id].name }));

  // Lab + Diagnostic: first ranked topic that actually has each.
  let lab: Recommendation["lab"];
  for (const { t } of ranked) {
    if (!t.lab) continue;
    const l = LABS.find((x) => x.id === t.lab);
    if (l) { lab = { id: l.id, title: l.title }; break; }
  }
  let diagnostic: Recommendation["diagnostic"];
  for (const { t } of ranked) {
    if (!t.diagnostic) continue;
    const d = DIAGNOSTICS.find((x) => x.id === t.diagnostic);
    if (d) { diagnostic = { id: d.id, title: d.title }; break; }
  }

  return { frameworks, lab, diagnostic };
}
